import create from 'zustand';
import { Commodity } from '@enmon/commons';
import { InventorizeMeterStore } from '../useStore';
import { InventorizeStep as messages } from '../messages';

export enum InventorizeStep {
  LocationStep = 1,
  MapStep,
  FloorplanStep,
  DetailStep,
  ReadingStep,
  ReviewStep,
}

export const INVENTORIZE_STEPS_COUNT = Object.keys(InventorizeStep).length / 2;

const INITIAL_STATE = {
  currentStep: InventorizeStep.LocationStep,
} as const;

export const INVENTORIZE_STEP_TRANSLATED = {
  [InventorizeStep.LocationStep]: messages.locationStep,
  [InventorizeStep.MapStep]: messages.mapStep,
  [InventorizeStep.FloorplanStep]: messages.floorplanStep,
  [InventorizeStep.DetailStep]: messages.detailStep,
  [InventorizeStep.ReadingStep]: messages.readingStep,
  [InventorizeStep.ReviewStep]: messages.reviewStep,
} as const;

interface SetStepOptions {
  newMeter: InventorizeMeterStore['newMeter'];
  commodity: InventorizeMeterStore['commodity'];
}

const getNextStep = (currentStep: InventorizeStep, options: SetStepOptions) => {
  const { newMeter, commodity } = options;
  switch (currentStep) {
    case InventorizeStep.LocationStep:
      return InventorizeStep.MapStep;
    case InventorizeStep.MapStep:
      return InventorizeStep.FloorplanStep;
    case InventorizeStep.FloorplanStep:
      return InventorizeStep.DetailStep;
    case InventorizeStep.DetailStep:
      if (newMeter || commodity === Commodity.Other) {
        return InventorizeStep.ReviewStep;
      }
      return InventorizeStep.ReadingStep;
    default:
      return InventorizeStep.ReviewStep;
  }
};

const getPreviousStep = (currentStep: InventorizeStep, options: SetStepOptions) => {
  const { newMeter, commodity } = options;
  switch (currentStep) {
    case InventorizeStep.ReviewStep:
      if (newMeter || commodity === Commodity.Other) {
        return InventorizeStep.DetailStep;
      }
      return InventorizeStep.ReadingStep;
    case InventorizeStep.ReadingStep:
      return InventorizeStep.DetailStep;
    case InventorizeStep.DetailStep:
      return InventorizeStep.FloorplanStep;
    case InventorizeStep.FloorplanStep:
      return InventorizeStep.MapStep;
    default:
      return InventorizeStep.LocationStep;
  }
};

export interface InventorizeStepsStore {
  currentStep: InventorizeStep;
  setCurrentStep: (payload: InventorizeStep) => void;
  /**
   * Sets next inventorize step based on current step and state of inventorization, defaults to last step(ReviewStep)
   */
  setNextStep: (options: SetStepOptions) => void;
  /**
   * Sets previous inventorize step based on current step and state of inventorization, defaults to first
   * step(LocationStep)
   */
  setPreviousStep: (options: SetStepOptions) => void;
  reset: () => void;
}

export const useInventorizeStepsStore = create<InventorizeStepsStore>((set, get) => ({
  currentStep: InventorizeStep.LocationStep,
  setCurrentStep: (step: InventorizeStep) => {
    set(() => ({ currentStep: step }));
  },
  setNextStep: options => {
    const { currentStep } = get();
    const nextStep = getNextStep(currentStep, options);
    set(() => ({ currentStep: nextStep }));
  },
  setPreviousStep: options => {
    const { currentStep } = get();
    const previousStep = getPreviousStep(currentStep, options);
    set(() => ({ currentStep: previousStep }));
  },
  reset: () => set(INITIAL_STATE),
}));
